import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import "react-quill/dist/quill.snow.css";
import auth from "../../firebase.init";
import Pagination from "../../components/Shared/Pagination";
import { Helmet } from "react-helmet";
import LoadingAnimations from "../../components/Shared/LoadingAnimations";
import { SquarePen, Trash2, Plus, List } from "lucide-react";
import { toast } from 'react-hot-toast';
import { useSecureFetch } from "../../hooks/useSecureFetch";

// SubCategory Modal Component
const SubCategoryModal = ({ isOpen, onClose, onSubmit, editSubCategory = null, parentCategories }) => {
    const [formData, setFormData] = useState({
        subCategoryName: '',
        SEOTitle: '',
        subCategoryDetails: '',
        parentCategory: '',
        adminEmail: '',
        image: '',
    });
    const [user] = useAuthState(auth);

    // Update form data when editSubCategory changes
    useEffect(() => {
        if (editSubCategory) {
            setFormData({
                subCategoryName: editSubCategory.subCategoryName || '',
                SEOTitle: editSubCategory.SEOTitle || '',
                subCategoryDetails: editSubCategory.subCategoryDetails || '',
                parentCategory: editSubCategory.parentCategory || '',
                adminEmail: editSubCategory.adminEmail || '',
                image: editSubCategory.image || '',
            });
        } else {
            // Reset form when adding new subcategory
            setFormData({
                subCategoryName: '',
                SEOTitle: '',
                subCategoryDetails: '',
                parentCategory: '',
                adminEmail: '',
                image: '',
            });
        }
    }, [editSubCategory]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            ...formData,
            adminEmail: user?.email
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{editSubCategory ? 'Edit Subcategory' : 'Add New Subcategory'}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">Subcategory Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="subCategoryName"
                                            value={formData.subCategoryName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">SEO Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="SEOTitle"
                                            value={formData.SEOTitle}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label className="form-label">Parent Category</label>
                                        <select
                                            className="form-select"
                                            name="parentCategory"
                                            value={formData.parentCategory}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select parent category</option>
                                            {parentCategories.map((cat) => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.categoryName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="form-label">Image URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="image"
                                            value={formData.image}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="form-label">Subcategory Details</label>
                                        <textarea
                                            className="form-control"
                                            name="subCategoryDetails"
                                            value={formData.subCategoryDetails}
                                            onChange={handleChange}
                                            rows="4"
                                            required
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer mt-4">
                                <button type="button" className="btn btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editSubCategory ? 'Update Subcategory' : 'Add Subcategory'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Original Category Modal (with minor updates)
const CategoryModal = ({ isOpen, onClose, onSubmit, editCategory = null, uniqueMainCategories }) => {
    const [formData, setFormData] = useState({
        categoryName: '',
        SEOTitle: '',
        categoryDetails: '',
        mainCategory: '',
        adminEmail: '',
        image: '',
    });
    const [user] = useAuthState(auth);

    // Update form data when editCategory changes
    useEffect(() => {
        if (editCategory) {
            setFormData({
                categoryName: editCategory.categoryName || '',
                SEOTitle: editCategory.SEOTitle || '',
                categoryDetails: editCategory.categoryDetails || '',
                mainCategory: editCategory.mainCategory || '',
                adminEmail: editCategory.adminEmail || '',
                image: editCategory.image || '',
            });
        } else {
            // Reset form when adding new category
            setFormData({
                categoryName: '',
                SEOTitle: '',
                categoryDetails: '',
                mainCategory: '',
                adminEmail: '',
                image: '',
            });
        }
    }, [editCategory]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            ...formData,
            adminEmail: user?.email
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{editCategory ? 'Edit Category' : 'Add New Category'}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">Category Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="categoryName"
                                            value={formData.categoryName}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label">SEO Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="SEOTitle"
                                            value={formData.SEOTitle}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label className="form-label">Main Category</label>
                                        <select
                                            className="form-select"
                                            name="mainCategory"
                                            value={formData.mainCategory}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select main category</option>
                                            {["Graphic", "Templates", "Database", "Website", "eBook", "StockPhotos", "StockVideos", "Software"].map((cat, index) => (
                                                <option key={index} value={cat}>
                                                    {cat}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="form-label">Image URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="image"
                                            value={formData.image}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="form-label">Category Details</label>
                                        <textarea
                                            className="form-control"
                                            name="categoryDetails"
                                            value={formData.categoryDetails}
                                            onChange={handleChange}
                                            rows="4"
                                            required
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer mt-4">
                                <button type="button" className="btn btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editCategory ? 'Update Category' : 'Add Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Component with SubCategory support
const AdminMainCategory = () => {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [uniqueMainCategories, setUniqueMainCategories] = useState([]);
    const [user] = useAuthState(auth);
    const [activeTab, setActiveTab] = useState('all');
    const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'subcategories'
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
     const { secureFetch } = useSecureFetch();

    // Generate slug function
    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

const fetchData = async () => {
  setLoading(true);
  try {
    // Fetch categories
    const categoriesData = await secureFetch(`https://server.enjoywiki.com/flip-server/categories`);
    const reversedCategories = Array.isArray(categoriesData) ? categoriesData.reverse() : [];
    setCategories(reversedCategories);

    // Extract and format unique main categories
    const mainCats = [...new Set(reversedCategories.map(cat => cat.mainCategory))];
    const formattedMainCats = mainCats.map(name => ({
      _id: name.toLowerCase(),
      name,
      slug: name.toLowerCase()
    }));
    setUniqueMainCategories(formattedMainCats);

    // Fetch subcategories
    const subCategoriesData = await secureFetch(`https://server.enjoywiki.com/flip-server/subcategories`);
    const reversedSubCategories = Array.isArray(subCategoriesData) ? subCategoriesData.reverse() : [];
    setSubCategories(reversedSubCategories);
    
  } catch (error) {
    console.error("Error fetching data:", error);
    toast.error("Failed to fetch categories and subcategories");
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchData();
}, [user?.email, secureFetch]);


    // Handle category submission
    const handleCategorySubmit = async (formData) => {
        const slug = generateSlug(formData.categoryName);

        if (!editingCategory) {
            const existingCategory = categories.find(category => category.slug === slug);
            if (existingCategory) {
                toast.error("Category already exists");
                return;
            }
        }

        const categoryData = {
            ...formData,
            slug
        };

        const url = editingCategory
            ? `https://server.enjoywiki.com/flip-server/seller-category/${editingCategory._id}`
            : 'https://server.enjoywiki.com/flip-server/add-category';

        try {
            const response = await fetch(url, {
                method: editingCategory ? "PUT" : "POST",
                headers: {
                    "content-type": "application/json",
                },
                body: JSON.stringify(categoryData),
            });

            const result = await response.json();

            if (result.success || result.acknowledged || result.insertedId || result.modifiedCount > 0) {
                toast.success(`Category ${editingCategory ? 'updated' : 'added'} successfully`);
                fetchData();
                setEditingCategory(null);
                setIsModalOpen(false);
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('An error occurred');
        }
    };

    // Handle subcategory submission
    const handleSubCategorySubmit = async (formData) => {
        const slug = generateSlug(formData.subCategoryName);

        if (!editingSubCategory) {
            const existingSubCategory = subCategories.find(subCat => subCat.slug === slug);
            if (existingSubCategory) {
                toast.error("Subcategory already exists");
                return;
            }
        }

        // Find parent category details
        const parentCategory = categories.find(cat => cat._id === formData.parentCategory);
        
        const subCategoryData = {
            ...formData,
            slug,
            mainCategory: parentCategory ? parentCategory.mainCategory : '',
            parentCategoryName: parentCategory ? parentCategory.categoryName : ''
        };

        const url = editingSubCategory
            ? `https://server.enjoywiki.com/flip-server/seller-subcategory/${editingSubCategory._id}`
            : 'https://server.enjoywiki.com/flip-server/add-subcategory';

        try {
            const response = await fetch(url, {
                method: editingSubCategory ? "PUT" : "POST",
                headers: {
                    "content-type": "application/json",
                },
                body: JSON.stringify(subCategoryData),
            });

            const result = await response.json();

            if (result.success || result.acknowledged || result.insertedId || result.modifiedCount > 0) {
                toast.success(`Subcategory ${editingSubCategory ? 'updated' : 'added'} successfully`);
                fetchData();
                setEditingSubCategory(null);
                setIsSubCategoryModalOpen(false);
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('An error occurred');
        }
    };

    // Filter categories based on active tab
    const getFilteredCategories = () => {
        if (activeTab === 'all') {
            return categories;
        }
        return categories.filter(cat => cat.mainCategory.toLowerCase() === activeTab);
    };

    // Filter subcategories based on active tab and selected category
    const getFilteredSubCategories = () => {
        let filtered = subCategories;
        
        if (activeTab !== 'all') {
            filtered = filtered.filter(subCat => subCat.mainCategory.toLowerCase() === activeTab);
        }
        
        if (selectedCategory) {
            filtered = filtered.filter(subCat => subCat.parentCategory === selectedCategory._id);
        }
        
        return filtered;
    };

    const filteredCategories = getFilteredCategories();
    const filteredSubCategories = getFilteredSubCategories();

    // Get count of categories for each main category
    const getCategoryCount = (mainCategorySlug) => {
        return categories.filter(cat => cat.mainCategory.toLowerCase() === mainCategorySlug).length;
    };

    // Get count of subcategories for each category
    const getSubCategoryCount = (categoryId) => {
        return subCategories.filter(subCat => subCat.parentCategory === categoryId).length;
    };

    // Handle delete category
    const handleDeleteCategory = (categoryId) => {
        // Check if category has subcategories
        const hasSubcategories = subCategories.some(subCat => subCat.parentCategory === categoryId);
        
        if (hasSubcategories) {
            toast.error("Cannot delete category with subcategories. Delete subcategories first.");
            return;
        }
        
        if (window.confirm("Are you sure you want to delete this category?")) {
            fetch(`https://server.enjoywiki.com/flip-server/seller-category/${categoryId}`, {
                method: "DELETE",
            })
                .then((res) => res.json())
                .then(() => {
                    const updatedCategories = categories.filter((cat) => cat._id !== categoryId);
                    setCategories(updatedCategories);
                    toast.success("Category deleted successfully");
                })
                .catch((error) => {
                    console.error("Error deleting category:", error);
                });
        }
    };

    // Handle delete subcategory
    const handleDeleteSubCategory = (subCategoryId) => {
        if (window.confirm("Are you sure you want to delete this subcategory?")) {
            fetch(`https://server.enjoywiki.com/flip-server/seller-subcategory/${subCategoryId}`, {
                method: "DELETE",
            })
                .then((res) => res.json())
                .then(() => {
                    const updatedSubCategories = subCategories.filter((subCat) => subCat._id !== subCategoryId);
                    setSubCategories(updatedSubCategories);
                    toast.success("Subcategory deleted successfully");
                })
                .catch((error) => {
                    console.error("Error deleting subcategory:", error);
                });
        }
    };

    const handleViewSubcategories = (category) => {
        setSelectedCategory(category);
        setViewMode('subcategories');
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = viewMode === 'categories' 
        ? filteredCategories.slice(indexOfFirstItem, indexOfLastItem)
        : filteredSubCategories.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    // Reset pagination when changing tabs or view mode
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, viewMode, selectedCategory]);

    return (
        <main className="dashboard-main min-vh-100">
            <div className="d-flex flex-column gap-4 pb-110 p-4">
                <Helmet>
                    <title>
                        {viewMode === 'categories' 
                            ? 'All Categories List | FlipBundle' 
                            : `Subcategories of ${selectedCategory?.categoryName || 'Category'} | FlipBundle`}
                    </title>
                    <meta name="description" content="View and manage your digital product listings on FlipBundle. Easily update, promote, and track your products for increased sales." />
                </Helmet>

                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="text-24 fw-bold text-dark-300 mb-2">
                            {viewMode === 'categories' 
                                ? 'Main Categories List' 
                                : `Subcategories of ${selectedCategory?.categoryName || 'Category'}`}
                        </h3>
                        <ul className="d-flex align-items-center gap-2">
                            <li className="text-dark-200 fs-6">Admin Dashboard</li>
                            <li>
                                <svg xmlns="http://www.w3.org/2000/svg" width={5} height={11} viewBox="0 0 5 11" fill="none">
                                    <path d="M1 10L4 5.5L1 1" stroke="#5B5B5B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </li>
                            {viewMode === 'subcategories' && (
                                <>
                                    <li className="text-dark-200 fs-6" style={{ cursor: 'pointer' }} onClick={() => setViewMode('categories')}>
                                        All Categories
                                    </li>
                                    <li>
                                        <svg xmlns="http://www.w3.org/2000/svg" width={5} height={11} viewBox="0 0 5 11" fill="none">
                                            <path d="M1 10L4 5.5L1 1" stroke="#5B5B5B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </li>
                                </>
                            )}
                            <li className="text-lime-300 fs-6">
                                {viewMode === 'categories' ? 'All Categories' : `Subcategories`}
                            </li>
                        </ul>
                    </div>
                    {viewMode === 'categories' ? (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="d-flex align-items-center gap-2 w-btn-secondary-lg">
                            <Plus size={16} />
                            Add Category
                        </button>
                    ) : (
                        <div className="d-flex gap-2">
                            <button
                                onClick={() => setViewMode('categories')}
                                className="d-flex align-items-center gap-2 btn btn-outline-secondary">
                                <List size={16} />
                                Back to Categories
                            </button>
                            <button
                                onClick={() => setIsSubCategoryModalOpen(true)}
                                className="d-flex align-items-center gap-2 w-btn-secondary-lg">
                                <Plus size={16} />
                                Add Subcategory
                            </button>
                        </div>
                    )}
                </div>

                {/* Filter Tabs */}
                <div>
                    <nav>
                        <div className="d-flex flex-wrap gap-3 align-items-center" role="tablist">
                            <button
                                className={`dashboard-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                                onClick={() => setActiveTab('all')}
                            >
                                All {viewMode === 'categories' ? 'Categories' : 'Subcategories'} ({viewMode === 'categories' ? categories.length : subCategories.length})
                            </button>

                            {loading ? (
                                <LoadingAnimations type="pulse" color="#2563eb" size="sm" />
                            ) : uniqueMainCategories.length > 0 ? (
                                uniqueMainCategories.map((mainCat) => (
                                    <button
                                        key={mainCat._id}
                                        className={`dashboard-tab-btn ${activeTab === mainCat.slug ? 'active' : ''}`}
                                        onClick={() => setActiveTab(mainCat.slug)}
                                    >
                                        {mainCat.name} ({viewMode === 'categories' 
                                            ? getCategoryCount(mainCat.slug) 
                                            : subCategories.filter(subCat => subCat.mainCategory.toLowerCase() === mainCat.slug).length})
                                    </button>
                                ))
                            ) : (
                                <p className="text-muted">No main categories found</p>
                            )}
                        </div>
                    </nav>

                    {/* Content */}
                    <div className="gig-info-card mt-4">
                        <div className="gig-info-body">
                            <div className="overflow-x-auto">
                                <div className="w-100">
                                    {viewMode === 'categories' ? (
                                        // Categories Table
                                        <table className="w-100 dashboard-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col" className="ps-4">Category</th>
                                                    <th scope="col">Main Category</th>
                                                    <th scope="col">Subcategories</th>
                                                    <th scope="col">Edit</th>
                                                    <th scope="col" className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center p-4">
                                                            <LoadingAnimations
                                                                type="pulse"
                                                                color="#2563eb"
                                                                size="md"
                                                            />
                                                        </td>
                                                    </tr>
                                                ) : currentItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center p-4">
                                                            No Category Found!
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    currentItems.map((category) => (
                                                        <tr key={category._id}>
                                                            <td>
                                                                <div className="d-flex gap-3 align-items-center project-name">
                                                                    <div className="order-img">
                                                                        {category.image && (
                                                                            <img
                                                                                src={category.image}
                                                                                alt={category.categoryName}
                                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {category.categoryName}
                                                                        <ul className="d-flex gap-1 order-category mt-1">
                                                                            {category.slug && <li className="text-dark-200">{category.slug}</li>}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <p className="status-badge in-progress font-bold">
                                                                    {category.mainCategory}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-sm btn-info"
                                                                    onClick={() => handleViewSubcategories(category)}
                                                                >
                                                                    {getSubCategoryCount(category._id)} Subcategories
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingCategory(category);
                                                                        setIsModalOpen(true);
                                                                    }}
                                                                    className="btn btn-primary btn-sm"
                                                                >
                                                                    <SquarePen size={15} />
                                                                    {' '}Edit
                                                                </button>
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    onClick={() => handleDeleteCategory(category._id)}
                                                                    className="btn btn-danger btn-sm"
                                                                >
                                                                    <Trash2 size={15} />
                                                                    {' '}Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    ) : (
                                        // Subcategories Table
                                        <table className="w-100 dashboard-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col" className="ps-4">Subcategory</th>
                                                    <th scope="col">Parent Category</th>
                                                    <th scope="col">Main Category</th>
                                                    <th scope="col">Edit</th>
                                                    <th scope="col" className="text-center">Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center p-4">
                                                            <LoadingAnimations
                                                                type="pulse"
                                                                color="#2563eb"
                                                                size="md"
                                                            />
                                                        </td>
                                                    </tr>
                                              // Continuing from where we left off - the subcategories table body
                                            ) : currentItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center p-4">
                                                        No Subcategories Found!
                                                    </td>
                                                </tr>
                                            ) : (
                                                currentItems.map((subCategory) => (
                                                    <tr key={subCategory._id}>
                                                        <td>
                                                            <div className="d-flex gap-3 align-items-center project-name">
                                                                <div className="order-img">
                                                                    {subCategory.image && (
                                                                        <img
                                                                            src={subCategory.image}
                                                                            alt={subCategory.subCategoryName}
                                                                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    {subCategory.subCategoryName}
                                                                    <ul className="d-flex gap-1 order-category mt-1">
                                                                        {subCategory.slug && <li className="text-dark-200">{subCategory.slug}</li>}
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="status-badge success font-bold">
                                                                {subCategory.parentCategoryName}
                                                            </p>
                                                        </td>
                                                        <td>
                                                            <p className="status-badge in-progress font-bold">
                                                                {subCategory.mainCategory}
                                                            </p>
                                                        </td>
                                                        <td>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingSubCategory(subCategory);
                                                                    setIsSubCategoryModalOpen(true);
                                                                }}
                                                                className="btn btn-primary btn-sm"
                                                            >
                                                                <SquarePen size={15} />
                                                                {' '}Edit
                                                            </button>
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                onClick={() => handleDeleteSubCategory(subCategory._id)}
                                                                className="btn btn-danger btn-sm"
                                                            >
                                                                <Trash2 size={15} />
                                                                {' '}Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Pagination */}
                        {(viewMode === 'categories' ? filteredCategories.length : filteredSubCategories.length) > itemsPerPage && (
                            <div className="pagination-container mt-4 d-flex justify-content-center">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={Math.ceil((viewMode === 'categories' ? filteredCategories.length : filteredSubCategories.length) / itemsPerPage)}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Category Modal */}
            <CategoryModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCategory(null);
                }}
                onSubmit={handleCategorySubmit}
                editCategory={editingCategory}
                uniqueMainCategories={uniqueMainCategories}
            />

            {/* SubCategory Modal */}
            <SubCategoryModal
                isOpen={isSubCategoryModalOpen}
                onClose={() => {
                    setIsSubCategoryModalOpen(false);
                    setEditingSubCategory(null);
                }}
                onSubmit={handleSubCategorySubmit}
                editSubCategory={editingSubCategory}
                parentCategories={categories}
            />
        </div>
    </main>
);
};

export default AdminMainCategory;